/**
 * Cents-first amount entry: digits shift in from the right, so 1046 shows 10.46 and 1 shows 0.01.
 * Used only by the amount of a purchase (add and edit, and an auto-logged charge's New purchases
 * Edit sheet: `ns-amt`) and the "Different amount" sheet; planned
 * amounts keep normal entry. To change the scope, add or remove `centsField()` in a view.
 *
 * - Only digits; at most CENTS_MAX_DIGITS of them. Backspace removes the last digit.
 * - A pre-filled value (a planned amount, the purchase being edited) is replaced by the first
 *   digit and cleared by the first backspace, as is a value that is all selected.
 * - A "." or "," switches the field to normal decimal entry until the sheet closes ("10.46" typed
 *   the old way still works; parseCents() reads it). A paste ("$1,046.50") is parsed and fills it.
 *
 * This module is pure (tested); cents-input.ts wires it to the DOM (main.ts only).
 */
import { parseCents } from './format';

export const CENTS_MAX_DIGITS = 9;

export interface CentsState {
  value: string;
  /** Pre-filled and not edited yet: the first digit replaces it, the first backspace clears it. */
  pristine: boolean;
  /** Switched to normal decimal entry by a "." or ",". */
  decimal: boolean;
}

export type CentsEdit = { kind: 'insert'; text: string; all?: boolean } | { kind: 'delete'; all?: boolean } | { kind: 'paste'; text: string };

/** "1046" -> "10.46"; "" -> "" (the placeholder shows 0.00). */
export const formatDigits = (digits: string) => (digits ? (Number(digits) / 100).toFixed(2) : '');

/** The digits behind a shown value: "10.46" -> "1046", "250.00" -> "25000", "" -> "". */
export function digitsOf(value: string): string {
  const c = parseCents(value);
  return Number.isFinite(c) && c > 0 ? String(c) : '';
}

/** The state an amount field starts in: a value is pre-filled. */
export const centsStart = (value: string): CentsState => ({ value, pristine: value !== '', decimal: false });

/**
 * One edit. Returns the next state, or null when the browser should apply it itself (typing and
 * deleting in decimal mode).
 */
export function editCents(s: CentsState, e: CentsEdit): CentsState | null {
  if (e.kind === 'paste') {
    const c = parseCents(e.text);
    if (!Number.isFinite(c) || c < 0 || String(c).length > CENTS_MAX_DIGITS) return s;
    return { value: c > 0 ? (c / 100).toFixed(2) : '', pristine: false, decimal: false };
  }
  if (s.decimal) return null;
  const fresh = s.pristine || !!e.all;
  if (e.kind === 'delete') return { value: fresh ? '' : formatDigits(digitsOf(s.value).slice(0, -1)), pristine: false, decimal: false };
  let digits = fresh ? '' : digitsOf(s.value);
  const chars = [...e.text];
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (ch === '.' || ch === ',') {
      // Normal decimal entry from here on: what was typed so far is whole dollars.
      const rest = chars.slice(i + 1).filter((x) => /[\d.,]/.test(x)).join('');
      return { value: `${digits || '0'}${ch}${rest}`, pristine: false, decimal: true };
    }
    if (!/\d/.test(ch)) continue;
    const next = (digits + ch).replace(/^0+/, '');
    if (next.length > CENTS_MAX_DIGITS) break;
    digits = next;
  }
  if (!fresh && digits === digitsOf(s.value)) return s; // nothing it could take (a letter, the cap)
  return { value: formatDigits(digits), pristine: false, decimal: false };
}

/**
 * The amount input for the three sheets: number pad, cents-first, and a polite live region that
 * says the formatted amount ("$10.46") as it changes.
 */
export function centsField(id: string, cents: number | null, extra = ''): string {
  const value = cents ? (cents / 100).toFixed(2) : '';
  return `<span class="sr" aria-live="polite" id="${id}-said"></span><input id="${id}" inputmode="decimal" placeholder="0.00" value="${value}"${extra} data-cents${value ? ' data-pristine="1"' : ''} autocomplete="off" enterkeyhint="done" aria-describedby="${id}-said">`;
}
